import { TSupportMessage } from '../../services/resources/supportChat/types'
import {
  isFirebaseConfigured,
  signInWithBackendToken,
  subscribeToMessages,
  subscribeToMeta
} from '../../services/firebase'
import { useEffect, useRef, useState } from 'react'

export type TChatMessage = TSupportMessage & { id: string }

type UseConversationMessagesParams = {
  conversationUuid: string | null
  firebaseToken?: string
  /** Chamado quando chega mensagem de outra pessoa (para som e aviso visual). */
  onIncoming?: (message: TChatMessage) => void
}

/**
 * Autentica no Firebase e escuta uma conversa.
 *
 * Os listeners são recriados a cada troca de conversa e cancelados no cleanup —
 * sem isso o listener antigo sobrevive e as mensagens de duas conversas se
 * misturam na tela.
 */
export const useConversationMessages = ({
  conversationUuid,
  firebaseToken,
  onIncoming
}: UseConversationMessagesParams) => {
  const [messages, setMessages] = useState<TChatMessage[]>([])
  const [meta, setMeta] = useState<Record<string, any> | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Guarda o callback em ref: assim trocar a função não recria os listeners.
  const onIncomingRef = useRef(onIncoming)
  useEffect(() => {
    onIncomingRef.current = onIncoming
  }, [onIncoming])

  // As primeiras mensagens chegam pelo mesmo evento das novas (onChildAdded).
  // Este flag evita tocar o som uma vez por mensagem do histórico ao abrir.
  const loadedInitialRef = useRef(false)

  useEffect(() => {
    if (!conversationUuid || !firebaseToken) {
      setMessages([])
      setMeta(null)
      setIsReady(false)

      return undefined
    }

    if (!isFirebaseConfigured()) {
      setError('Firebase não configurado neste ambiente')

      return undefined
    }

    let cancelled = false
    let unsubscribeMessages: (() => void) | undefined
    let unsubscribeMeta: (() => void) | undefined

    setMessages([])
    setMeta(null)
    setIsReady(false)
    setError(null)
    loadedInitialRef.current = false

    // Depois de um tempinho sem mensagem nova, o que vier é ao vivo.
    const initialTimer = window.setTimeout(() => {
      loadedInitialRef.current = true
    }, 1500)

    signInWithBackendToken(firebaseToken)
      .then(() => {
        if (cancelled) return

        unsubscribeMessages = subscribeToMessages(conversationUuid, (message: TChatMessage) => {
          setMessages((current) => (current.some((item) => item.id === message.id) ? current : [...current, message]))

          if (loadedInitialRef.current && message.sender_type !== 'patient') {
            onIncomingRef.current?.(message)
          }
        })

        unsubscribeMeta = subscribeToMeta(conversationUuid, (value) => setMeta(value))
        setIsReady(true)
      })
      .catch((authError: Error) => {
        if (!cancelled) setError(authError.message)
      })

    return () => {
      cancelled = true
      window.clearTimeout(initialTimer)
      unsubscribeMessages?.()
      unsubscribeMeta?.()
    }
  }, [conversationUuid, firebaseToken])

  return { messages, meta, isReady, error }
}

export default useConversationMessages
